import { useEffect, useState } from "react";
import { Gift, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { adminApi } from "@/lib/auth";
import { toast } from "sonner";

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const isFinished = (c) => {
  const last = [c.date1, c.date2, c.date3].filter(Boolean).sort().pop();
  return !!last && last < todayIso();
};

// Reciclaje y PPAA: el instructor decide si entrega mascarillas en el curso.
export const MasksDecisionPanel = ({ course, onDecided }) => {
  const [saving, setSaving] = useState(false);
  const [askOpen, setAskOpen] = useState(false);
  const applies = ["reciclaje", "ppaa"].includes(course?.course_type);
  const finished = applies && isFinished(course);
  const decision = course?.masks_decision;

  useEffect(() => {
    setAskOpen(applies && !finished && (decision === null || decision === undefined));
  }, [course?.id, applies, finished, decision]);

  if (!applies) return null;

  const decide = async (give) => {
    setSaving(true);
    try {
      await adminApi.post(`/instructor/courses/${course.id}/masks-decision`, { give_masks: give });
      onDecided(course.id, give);
      setAskOpen(false);
      toast.success(give ? "Anotado: entregas mascarillas en este curso." : "Anotado: no entregas mascarillas.");
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "No se pudo guardar.");
    } finally {
      setSaving(false);
    }
  };

  const choice = (give, testid) => (
    <Button
      type="button"
      onClick={() => decide(give)}
      disabled={saving || finished}
      variant={decision === give ? "default" : "outline"}
      className={`h-11 px-5 font-bold ${decision === give ? "bg-zinc-950 text-white" : "border-2 border-zinc-300"}`}
      data-testid={testid}
    >
      {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
      {give ? "SÍ, ENTREGO" : "NO ENTREGO"}
    </Button>
  );

  return (
    <>
      <div
        className={`mt-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3 rounded-md border-2 p-4 ${
          decision === null || decision === undefined ? "bg-amber-50 border-amber-400" : "bg-zinc-50 border-zinc-300"
        }`}
        data-testid="instructor-masks-decision-panel"
      >
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-1 inline-flex items-center gap-2">
            <Gift className="h-3.5 w-3.5" />
            Mascarillas
          </div>
          <h3 className="heading-font text-lg font-extrabold text-zinc-950" data-testid="masks-decision-status">
            {decision === true
              ? "Entregas mascarillas en este curso"
              : decision === false
                ? "No entregas mascarillas en este curso"
                : "¿Vas a entregar mascarillas en este curso?"}
          </h3>
          <p className="text-xs text-zinc-500 mt-1">
            {finished
              ? "El curso ya ha terminado: no se puede cambiar."
              : "Puedes cambiarlo mientras el curso no haya terminado."}
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          {choice(true, "masks-decision-yes")}
          {choice(false, "masks-decision-no")}
        </div>
      </div>

      <Dialog open={askOpen} onOpenChange={setAskOpen}>
        <DialogContent className="max-w-md" data-testid="masks-decision-dialog">
          <DialogHeader>
            <DialogTitle>¿Entregas mascarillas en este curso?</DialogTitle>
            <DialogDescription>
              {course.course_type === "ppaa" ? "Curso de Primeros Auxilios" : "Curso de reciclaje"} en{" "}
              {course.client_name || course.name}. Si dices que no, no se descuentan de tu stock.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            {choice(false, "masks-dialog-no")}
            {choice(true, "masks-dialog-yes")}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MasksDecisionPanel;
